import {
  retrievePublic,
  UberResponse,
  mmLogoutUser,
  modifyLayout,
  updateThemeObjForRecordingSession,
  toggleChat,
  toggleTicker,
  ToggleTickerRequestBody,
  updateRunOfShowConfig,
  updateOnDemandRunOfShowConfig,
  createCue,
  deleteCue,
  deleteAllBroadcastsCues,
  listCues,
  patchCue,
  resetAllBroadcastsCues,
  reorderCue,
  bulkUpdateCueProperties,
  type Cue,
  type CueCreateRequest,
  type CuePropertyUpdateRequest,
  type CueReorderRequest,
  BroadcastUpdateOnDemandRunOfShowConfigRequest
} from '@goldcast/api/backend';
import {
  GenericMessageDialog,
  HybridDialog,
  PopUpCreateTickerDialog,
  themeHelper,
  SessionChangeConfirmation
} from 'uicomponents';

import { EventGetters, getAgendaItemType } from './getters';
import { EventMutations } from './mutations';
import {
  EventState,
  AgendaItemType,
  Booth,
  AgendaItem,
  EventUser,
  RawBooth,
  BareUser,
  BareEventSpeaker,
  Broadcast,
  TrackItem,
  TicketType,
  User
} from './types';
import { endEvent, formatAgendaItem } from './utils';

import analytics from '@/analytics';
import ConfirmationPopup from '@/components/core/ConfirmationPopup.vue';
import { EMBEDDED_SESSION_STORAGE_KEY, getCloudFrontUrl, getEnv, AGORA } from '@/constants';
import errors from '@/constants/errors';
import i18n from '@/i18n';
import { mattermostApiClient } from '@/services/apiClient';
import DialogService from '@/services/dialogs/DialogService';
import sendEmbeddedEventMessage, { EmbeddedMessageTypeEnum } from '@/services/embedded-event.service';
import {
  attendEventAsync,
  getEventFromUberEndpoint,
  getEventUsers,
  startUberMmidPolling,
  stopUberMmidPolling,
  startUberPollingForRecorder,
  stopUberPollingForRecorder,
  getEventUsersById,
  getTracks,
  loadDiscussionGroupByID,
  loadDiscussionGroups,
  LoadEventBooths,
  startDiscussionRoomRecording,
  toggleAgendaItemLiveness,
  toggleBroadcastLiveness,
  getSurveyIframeConfig,
  transformParticipantToUser,
  updateBroadcast,
  updateEvent,
  updateEventUser
} from '@/services/event.service';
import { initializeFeatureFlags } from '@/services/featureFlag.service';
import loggerService from '@/services/logger.service';
import { ActivityType, handleSpeakerActivity } from '@/services/speakerActivity.service';
import { getSessionOnlineAttendeeCounts } from '@/services/unauthedapis.service';
import { UserRole } from '@/services/user.service';
import { RootState } from '@/store';
import { gotoNextPage, onConfirm, saveData } from '@/utils/eventActionUtils';
import { pushRoute, replaceRoute } from '@/utils/routerAccess';
import { safeSignout } from '@/utils/safeSignout';
import { clearSocketSessionMarkers } from '@/utils/socketSessionMarker';
import { getToastDisplayDelay } from '@/utils/stage';
import { makeUser, makeUsers } from '@/utils/userTransform';
import { NewLayoutNamesMap } from '@/views/event/BackstageControls/Toolbar/Layout/constants';
import { SESSION_ACTIONS } from '@/views/event/BackstageControls/Toolbar/LiveRunOfShow/config/constants';
export interface EventContext {
  state: EventState;
  rootState: RootState;
  getters: EventGetters;
  rootGetters: { [k: string]: any };
  commit: <k extends keyof EventMutations>(mutation: k, payload?: any, config?: { root: boolean }) => any;
  dispatch: (action: string, params?: any, settings?: { root: boolean }) => Promise<any>;
}

export type ManualSessionRedirectionType = {
  sessionId: string;
  sessionType: AgendaItemType;
  actionType: 'start' | 'end';
};

// Live Run of Show: Common helper to validate and return current broadcast ID
function getBroadcastId(context: EventContext, errorMessage: string): string {
  const broadcastId = context.rootGetters['event/currentBroadcast']?.id;

  if (!broadcastId || Array.isArray(broadcastId)) {
    loggerService.error(errorMessage);
    context.commit('setCueError', errorMessage);
    throw new Error(errorMessage);
  }
  return broadcastId as string;
}

/**
 * Cvent identity ids the survey endpoint now returns (WEBINAR-67990). These are
 * not yet present on the published `@goldcast/api` `GetCventEventSurveyOkResponse`
 * type, so the survey response is widened locally at the call site until the API
 * client package is regenerated and republished.
 */
export interface CventSurveyIdentityFields {
  cvent_session_id?: string | null;
  cvent_account_mapping_id?: string | null;
}

const actions = {
  // TODO: loadEventBoothsGridPage2 and loadEventBoothsGridPage below does
  // the exact same thing.
  // I am not sure of the context of why this was done.
  // I am not refactoring it because I do not want anything to break because
  // I am in a rush right now. This needs revisit. @soumya
  loadEventBoothsGridPage2(context: EventContext, params: any) {
    return new Promise((resolve, reject) => {
      LoadEventBooths(params)
        .then((response: any) => {
          return context.dispatch('makeFormattedBooths', response?.data || []);
        })
        .then((formattedBooths: Booth[]) => {
          const secondaryBooths = context.rootState.event?.event.secondaryBooths;

          const { enable_booth_tiers } = context.state.event;

          if (!enable_booth_tiers) {
            secondaryBooths['TIER1'].booths = formattedBooths;
          } else {
            Object.keys(secondaryBooths).forEach((key: string) => {
              secondaryBooths[key].booths = formattedBooths.filter((booth: Booth) => booth.tier === key);
            });
          }
          context.commit('setSecondaryBooths', secondaryBooths);
          return formattedBooths;
        })
        .then((formattedBooths: Booth[]) => {
          return resolve(formattedBooths);
        })
        .catch(err => {
          loggerService.error('Error while getting formatted booth data for grid page 2!', err);
          reject(err);
        });
    });
  },
  async loadEventBoothsGridPage(context: EventContext, params: any) {
    return new Promise((resolve, reject) => {
      LoadEventBooths(params)
        .then((response: any) => {
          return context.dispatch('makeFormattedBooths', response?.data || []);
        })
        .then((formattedBooths: Booth[]) => {
          const primaryBooths = context.rootState.event?.event.primaryBooths;

          const { enable_booth_tiers } = context.state.event;

          if (!enable_booth_tiers) {
            primaryBooths['TIER1'].booths = formattedBooths;
          } else {
            Object.keys(primaryBooths).forEach((key: string) => {
              primaryBooths[key].booths = formattedBooths.filter((booth: Booth) => {
                return booth.tier === key;
              });
            });
          }

          context.commit('setPrimaryBooths', primaryBooths);
          return formattedBooths;
        })
        .then((formattedBooths: Booth[]) => {
          return resolve(formattedBooths);
        })
        .catch(err => {
          loggerService.error('Error while getting formatted booth data for grid page 1!', err);
          reject(err);
        });
    });
  },
  toggleBroadcastLiveness(context: EventContext, payload: { is_live: boolean; id: string; live_start_time: string }) {
    return toggleBroadcastLiveness(payload.id, payload.is_live, payload.live_start_time);
  },
  toggleAgendaItemLiveness(
    context: EventContext,
    payload: { is_live: boolean; id: string | number; live_start_time: string }
  ) {
    return toggleAgendaItemLiveness(payload.id, payload.is_live, payload.live_start_time);
  },
  async updateLayoutSettings(context: EventContext) {
    const handleLayoutChange = async () => {
      const currentBroadcastId = context.rootState.route.params?.id as string;
      const updatedLayoutSettings = context.state.updatedLayoutSettings;
      const currentLayoutSettings = context.getters.currentBroadcastLayoutSettings;
      const currentBroadcast = context.getters.currentBroadcast;

      if (currentBroadcast?.is_live) {
        analytics.track('stage_layout_changed', {
          eventId: context.rootState.route.params?.eventID,
          broadcastId: currentBroadcastId,
          updatedLayoutSettings,
          useNewLayouts: context.state.useNewLayouts
        });
      }

      //showNameCards key present in updatedLayoutSettings
      if (updatedLayoutSettings?.showNameCards !== undefined) {
        await updateThemeObjForRecordingSession({
          id: currentBroadcastId,
          body: {
            theme_obj: {
              name_card_display: updatedLayoutSettings?.showNameCards ? 'block' : 'none',
              name_card_background: updatedLayoutSettings?.showNameCards
                ? 'var(--theme-secondary-color)'
                : 'transparent'
            }
          }
        });
      }

      return modifyLayout({
        body: {
          layout_settings: {
            ...currentLayoutSettings,
            ...updatedLayoutSettings
          }
        },
        id: currentBroadcastId
      });
    };

    try {
      context.commit('updateIsSavingLayout', true);

      const changedLayout =
        context.state.updatedLayoutSettings?.['screen_share_layout'] ||
        context.state.updatedLayoutSettings?.['speaker_layout'] ||
        '';

      const changedLayoutName = NewLayoutNamesMap[changedLayout] || '';

      if (changedLayoutName) {
        await handleSpeakerActivity(
          () => handleLayoutChange(),
          ActivityType.LayoutChange,
          ActivityType.LayoutChangeFailed,
          {
            title: changedLayoutName
          }
        );
      } else {
        await handleLayoutChange();
      }

      context.commit('updateCurrentBroadcastLayoutSettings', { layoutSettings: {}, resetState: true });
    } catch (e) {
      loggerService.error('Error while updating layout settings!', e);
    } finally {
      context.commit('updateIsSavingLayout', false);
    }
  },
  getCurrentLiveAgendaItemIndex(context: EventContext) {
    const filters = context.rootGetters['filterBar/EventAgendaPage'];
    let broadcasts = context.rootGetters['event/broadcastsByDates']('EventAgendaPage')[filters.dateFilter?.value] || [];

    if (filters.dateFilter?.value === 'all') {
      broadcasts = context.rootGetters['event/broadcastsWithDates']('EventAgendaPage');
    }

    const itemsPerPage = filters.perPage || broadcasts.length;
    const currentPage = filters.currentPage === 0 ? 1 : filters.currentPage;

    let index = broadcasts.findIndex((agendaItem: { is_live: boolean }) => agendaItem.is_live === true);
    let pageNumber = 1;
    if (index === -1) {
      pageNumber = currentPage;
      index = 0;
    } else if (index === 0) {
      pageNumber = 1;
    } else {
      pageNumber = Math.ceil((index + 1) / itemsPerPage);
    }
    context.commit('setCurrentLiveAgendaIndex', index);
    context.dispatch(
      'filterBar/loadPage',
      {
        page: 'EventAgendaPage',
        value: pageNumber
      },
      { root: true }
    );
  },
  getUser(context: EventContext, { id, force }: { id: string; force: boolean }) {
    const user = context.getters.userById(id);
    if (user && !force) {
      return Promise.resolve(user);
    }
    return getEventUsersById(context.rootState.route.params.eventID?.toString(), [id])
      .then(({ data }: any) => {
        if (data?.length > 0) {
          context.dispatch('recieveUsersInBulk', data);
          return makeUser(data.pop());
        }
        return;
      })
      .catch(e => {
        loggerService.error('Error loading user with id ' + id, e);
        return null;
      });
  },
  joinAgendaItem(context: EventContext, item: AgendaItem) {
    const currentBroadcast = context.getters.currentEventBroadcast(context.rootState.route.params.id?.toString());
    const changingToDifferentAgendaItem = currentBroadcast?.id !== item.id;
    if (changingToDifferentAgendaItem) {
      (context.commit as any)(
        'setWatchLive',
        {
          id: item.id,
          setTo: false
        },
        { root: true }
      );
      (context.commit as any)(
        'setWatchLive',
        {
          id: currentBroadcast?.id,
          setTo: false
        },
        { root: true }
      );
    }
    context.dispatch('event/stage/applyTransition', item, { root: true });
  },
  // possible only if the event is public or the user is an organizer
  async registerToEventIfNotRegistered(
    context: EventContext,
    {
      eventID,
      isEventPublic,
      email,
      firstName,
      lastName
    }: {
      eventID: string;
      isEventPublic: boolean | undefined;
      email: string;
      firstName: string;
      lastName: string;
    }
  ) {
    if (email && eventID) {
      let canRegisterP = Promise.resolve(isEventPublic);
      if (isEventPublic === undefined) {
        canRegisterP = context
          .dispatch('fetchEventById', { eventID })
          .then(thisEvent => {
            return thisEvent.is_public;
          })
          .catch(err => {
            loggerService.error('Failed to fetch event when trying to ensure user is registered!', err);
            return false;
          });
      }

      return canRegisterP
        .then(canRegister => {
          if (!canRegister) {
            return context
              .dispatch('session/getEmailUserStatus', { email, event: eventID, status_only: true }, { root: true })
              .then(status => {
                return status?.isOrganizer;
              })
              .catch(err => {
                loggerService.error('Failed to fetch user email status', err);
                return false;
              });
          }
          return canRegister;
        })
        .then(canRegister => {
          if (canRegister) {
            return context
              .dispatch(
                'session/createUser',
                {
                  email,
                  eventID,
                  firstName,
                  lastName,
                  eventUserStatus: 'Registered'
                },
                { root: true }
              )
              .then(() => {
                return context.dispatch('loadEventUser', { eventID, force: true }, { root: true });
              })
              .then(user => {
                return user?.status?.registeredToEvent ?? false;
              });
          }
          if (!isEventPublic) {
            throw new Error(
              'You are not registered to the event. Please contact organizer for more details about this event.'
            );
          }
          throw new Error("This user can't be self registered to this event!!");
        });
    }
    throw new Error('Invalid Email / Event Id !');
  },

  // when unregistered user directly lands on event Url
  registerToEventOnEventUrlLanding(
    context: EventContext,
    { eventID, isEventPublic }: { eventID: string; isEventPublic?: boolean }
  ) {
    return context
      .dispatch('registerToEventIfNotRegistered', {
        eventID,
        email: context.rootState?.user?.currentUser?.email,
        isEventPublic
      })
      .then(isRegistered => {
        if (!isRegistered) {
          const err = new Error('Eventuser not found');
          (err as any).statusCode = 403;
          throw err;
        }
        return context.dispatch('recordAttendance', {
          eventID,
          reloadUser: true
        });
      })
      .catch(e => {
        loggerService.warn('Eventuser not found', e);
        const err = new Error('Eventuser not found');
        (err as any).statusCode = 403;
        throw err;
      });
  },

  //record Attendance of Users joining event
  recordAttendance(context: EventContext, { eventID, reloadUser }: { eventID: string; reloadUser: boolean }) {
    return context.dispatch('loadEventUser', { eventID, force: reloadUser }, { root: true }).then(user => {
      if (!user?.status.registeredToEvent) {
        loggerService.warn(
          'While recording attendance, the user was found to not be registered! Trying to register user.'
        );
        return context.dispatch('registerToEventOnEventUrlLanding', {
          eventID,
          isEventPublic: context.rootState.event?.event?.is_public
        });
      }
      return context.dispatch('setMemberAsAttended', user);
    });
  },
  // If the breakdown of then chain seems arbitrary here, the principle is
  // that if a nested then is unavoidable then it should only return values
  // not do any computation. Any then doing a computation must be chained
  // not nested
  enterEvent(context: EventContext, { eventID }: { eventID: string }) {
    let onDemandMode: boolean;

    // Initialize feature flags first, then proceed with event loading
    return Promise.all([
      initializeFeatureFlags(true).catch(err => {
        // Log error but continue with defaults
        loggerService.error('Feature flag initialization failed, using defaults:', err);
      }),
      context.dispatch('fetchEventById', { eventID }).catch(e => {
        if (e.statusCode === 404 || e.statusCode === 403) {
          const err = {
            message: i18n.global.t(`@@translate@@ Failed to get event for eventID: {eventID}`, {
              eventID
            }),
            statusCode: e.statusCode,
            name: e.statusCode === 404 ? errors.EVENT_NOT_FOUND : errors.EVENT_ACCESS_NOT_ALLOWED
          };
          return Promise.reject(err);
        }
        return Promise.reject(e);
      })
    ])
      .then(() => {
        const isCventEvent = context.state.event?.cvent_event_id;
        const isRecorder =
          context.rootGetters['getCurrentUser']?.event_role === UserRole.RECORDER_BOT ||
          context.rootState.route?.query?.record_mode === 'true';

        if (isCventEvent && !isRecorder) {
          context.dispatch('fetchSurveyIframeConfig');
        }

        if (context.state.event?.is_ondemand) {
          onDemandMode = true;
        }
        let p1 = Promise.resolve();
        if (['bailed', 'bailed-noretry'].includes(context.rootState.mattermost.socketstatus)) {
          p1 = context.dispatch('mattermost/disconnect', null, {
            root: true
          });
        }
        const p2 = context.dispatch('apigateway/disconnect', null, {
          root: true
        });

        return Promise.allSettled([p1, p2]).then(allPromises => {
          loggerService.info('All promises in enter event: ', allPromises.map(p => p.status).join(','));
          return context.state.event;
        });
      })
      .then(() => {
        context.dispatch('apigateway/checkEventAndKick', 'kicked out from enter event', { root: true });
      })
      .then(() => {
        context.dispatch('recordAttendance', {
          eventID,
          reloadUser: false
        });
      })
      .then(() => {
        context.dispatch('setAppDataOnBrowser');
      })
      .then(() => {
        const isRecording = context.state.event.event_type === 'Recording';
        if (!isRecording) {
          context.dispatch('event/eventHeartbeat/initEventHeartbeat', null, {
            root: true
          });
        }
        context.dispatch('event/stage/startHeartBeat', null, {
          root: true
        });
        context.dispatch('mediaroom/fetchBulkAgoraToken', null, { root: true });
        return context.dispatch('mattermost/connect', null, { root: true });
      })
      .then(() => {
        if (!onDemandMode) {
          context.dispatch(
            'apigateway/subscribeToEvent',
            {
              eventId: eventID
            },
            {
              root: true
            }
          );
        }
        return context.dispatch('mattermost/setPublicChannels', context.state.event, {
          root: true
        });
      })
      .catch(e => {
        loggerService.warn('enterEvent: unexpected error after event loaded (MM or downstream step)', e);
        return Promise.reject(e);
      });
  },
  loadFonts(context: EventContext, event: any) {
    try {
      event = event || context.state.event;
      themeHelper.loadFont(event.theme_obj.font_type, event.theme_obj.theme_font_src, event.theme_obj.theme_font_link);
      themeHelper.loadFont(
        event.theme_obj.splash_font_type,
        event.theme_obj.splash_theme_font_src,
        event.theme_obj.splash_theme_font_link
      );
    } catch (error) {
      loggerService.error('Error loading fonts', { error });
    }
  },
  setLocale(context: EventContext, event: { app_ui_language: string; id: string }) {
    const locale = localStorage.getItem(`${event.id}-current-language`) || event.app_ui_language || 'en';
    context.commit('updateLocalLanguage', locale);
  },
  setMemberAsAttended(context: EventContext) {
    return attendEventAsync(context.rootState.route.params.eventID || context.state.event.id);
  },
  recieveUsersInBulk(context: EventContext, users: EventUser[]) {
    // Chunk out users into 5000 people blocks if there are too many
    const numChunks = Math.ceil(users.length / 5000);
    let chunkIndices = new Array(numChunks).fill(0).map((it, i) => i * 5000);
    if (chunkIndices.length === 0) {
      chunkIndices = [0];
    } else if (chunkIndices.length > 1) {
      loggerService.info('Loading more than 5000 users. Processing in chunks.');
    }
    const additionPromises = [];
    for (const startIndex of chunkIndices) {
      /*
       * This is a hack to prevent the agenda from reformatting
       * when we add users in bulk. This is because the agenda
       * is not reactive and we don't want to reformat it
       * every time we add a user. Only when speakers or organizers are added!
       */
      let shouldReformatAgenda = false;
      let endIndex = startIndex + 5000;
      if (endIndex > users.length) {
        endIndex = users.length;
      }
      const chunkOfUsers = users.slice(startIndex, endIndex);
      const additionPromise = new Promise(resolve => {
        setTimeout(() => {
          loggerService.info(`Adding batch ${startIndex} of users`);
          const madeUsers = makeUsers(chunkOfUsers);
          // TODO: Remove unused variable
          const usersMap = context.state.event.users || {};
          const usersToAdd = {};
          for (let userIndex = 0; userIndex < madeUsers.length; userIndex++) {
            const user = madeUsers[userIndex];
            const original = context.state.event.users[user.id] || {};
            const track = [user.track, original.track].find(t => t && t != 'None');
            const newUser = {
              ...original,
              ...user,
              track,
              eventUser: { ...original.eventUser, ...user.eventUser }
            };
            if (original.eventUser?.event_role && user.eventUser?.event_role === undefined) {
              newUser.eventUser.event_role = original.eventUser?.event_role;
            }
            if ([UserRole.ADMIN, UserRole.BROADCAST_SPEAKER, UserRole.STAFF].includes(newUser.eventUser?.event_role)) {
              shouldReformatAgenda = true;
            }

            usersToAdd[user.id] = newUser;
          }
          /*
          let usersMap = madeUsers.reduce(
            (acc: { [k: string]: User }, user: User) => {
              const original = context.state.event.users[user.id] || {};
              const track = [user.track, original.track].find(
                t => t && t != "None"
              );
              const newUser = {
                ...original,
                ...user,
                track,
                eventUser: { ...original.eventUser, ...user.eventUser }
              };
              if (
                original.eventUser?.event_role &&
                user.eventUser?.event_role === undefined
              ) {
                newUser.eventUser.event_role = original.eventUser?.event_role;
              }
              acc[user.id] = newUser;
              acc[user.mmid] = newUser;
              return acc;
            },
            context.state.event.users || {}
          );
          */

          context.commit('addUsersBulk', usersToAdd);
          if (shouldReformatAgenda) {
            const agendaItems = formatAgenda(
              context.state.event.agenda,
              context.state.event.tracks,
              context.state.event.users
            );
            agendaItems.broadcasts.forEach(item => setIsLiveAndTrigger(context, item));
            agendaItems.items.forEach(item => setIsLiveAndTrigger(context, item));
            context.commit('setEventAgenda', agendaItems);
          }

          loggerService.info(`Added batch ${startIndex} of users`);
          resolve({});
        });
      });
      //@ts-ignore
      additionPromises.push(additionPromise);
    }
    return Promise.all(additionPromises);
  },
  stopEventTicker(context: EventContext) {
    const eventId = context.rootState.route.params.eventID as string;
    const isAStaffMember = context.rootGetters['isStaffMember'];

    if (isAStaffMember) {
      return toggleTicker({
        id: eventId,
        body: {
          ticker_text: null,
          ticker_url: null,
          show_ticker: false
        }
      });
    }

    // TODO: Deprecate this after roles & permissions GA
    context.dispatch('updateEvent', {
      eventId: context.rootState.route.params.eventID,
      updatedParams: {
        ticker_text: null,
        ticker_url: null,
        show_ticker: false
      }
    });
  },
  openTickerDialog(context: EventContext) {
    const options = {
      placement: 'bottom-right',
      cancelOnClickOutside: false,
      on: {
        onSubmit: async ({ text1, text2 }: any) => {
          analytics.track('event_ticker_shared', {
            tickerText: text1,
            tickerUrl: text2,
            event_id: context.rootState.route.params?.eventID,
            userId: context.rootGetters['getCurrentUserId']
          });
          await context.dispatch('manageEventLevelTicker', {
            eventId: context.rootState.route.params.eventID,
            tickerParams: {
              ticker_text: text1,
              ticker_url: text2,
              show_ticker: true
            }
          });
        }
      }
    };
    DialogService.show(
      PopUpCreateTickerDialog,
      {
        heading: '@@translate@@ Create Ticker',
        subHeading:
          '@@translate@@ The ticker will appear across the top of the webpage for all attendees. To turn the ticker into a hyperlink enter the URL below',
        textPlaceholder1: '@@translate@@ Enter Message',
        textPlaceholder2: '@@translate@@ https://www.xyz.com',
        charLimit1: 50,
        buttonText: '@@translate@@ Launch'
      },
      options
    );
  },
  loadEventFromUber(
    context: EventContext,
    payload: {
      eventID: string;
      nofmmid?: boolean;
      forceUpdate?: boolean;
    }
  ) {
    if (!payload.eventID) {
      loggerService.warn('Event ID not found!');
      return;
    }

    let eventUsersLoaded: Promise<any> = Promise.resolve([]);
    if (!context.rootState.user.currentEventUser) {
      eventUsersLoaded = context
        .dispatch(
          'loadEventUser',
          {
            eventID: payload.eventID,
            force: true
          },
          { root: true }
        )
        .then(user => {
          return [user];
        });
    } else {
      eventUsersLoaded = Promise.resolve([context.rootState.user.currentEventUser]);
    }

    return new Promise((resolve, reject) => {
      if (context.state.event.dataLoaded && context.state.event?.id === payload?.eventID && !payload.forceUpdate) {
        loggerService.track('[API_CONSOLIDATION] uber skipped - dataLoaded cache hit', {
          eventId: payload?.eventID
        });
        resolve(context.state.event);
      } else {
        return getEventFromUberEndpoint(payload.eventID, payload?.forceUpdate)
          .then((res: any) => {
            context.commit('setLastUberFetchEpoch', Date.now());
            return saveData(context, eventUsersLoaded, res?.data);
          })
          .then(() => {
            if (['notstarted', 'error'].includes(context.rootState.mattermost.joinedChannels.state)) {
              const loadPromise = context.dispatch('mattermost/getJoinedChannels', null, { root: true });
              context.rootState.mattermost.joinedChannels.loadingpromise = loadPromise;
            }
            const { event } = context.rootState.event;
            loggerService.track('[API_CONSOLIDATION] uber loaded', {
              eventId: payload?.eventID,
              roleId: context.rootGetters['getCurrentUser']?.event_role,
              mmidPresent: !!event?.mmid,
              enableSpeakerTechCheck: event?.enable_speaker_tech_check,
              enableTechCheck: event?.enable_tech_check,
              showOnboarding: event?.show_onboarding
            });
            const isRecorderBot =
              context.rootGetters['getCurrentUser']?.event_role === UserRole.RECORDER_BOT ||
              context.rootState.route.query.record_mode === 'true';
            if (!event?.mmid && !isRecorderBot) {
              loggerService.error('[API_CONSOLIDATION] event.mmid after uber - mmid missing', {
                eventId: payload?.eventID
              });
            }
            if (isRecorderBot) {
              if (!event?.mmid) {
                startUberMmidPolling(context, payload.eventID);
              } else {
                startUberPollingForRecorder(payload.eventID);
              }
            }
            if (event?.host_type === 'In_Person') {
              replaceRoute({
                name: 'ErrorPage403',
                query: {
                  message: 'EVENT_ACCESS_NOT_ALLOWED'
                }
              });
            }
          })
          .then(resolve)
          .catch(reject);
      }
    });
  },
  makeFormattedBooths(context: EventContext, rawBooths: RawBooth[]) {
    return Promise.all(
      (rawBooths || []).map((rawBooth: RawBooth) => {
        return context.dispatch('makeFormattedBooth', rawBooth);
      })
    );
  },
  makeFormattedBooth(context: EventContext, rawBooth: RawBooth) {
    return new Promise((resolve, reject) => {
      if (!rawBooth?.event || !rawBooth?.id) {
        return resolve(rawBooth);
      }
      const captionsURL = `${getCloudFrontUrl('CLOUDFRONT_URL')}event/${rawBooth?.event}/booths/${
        rawBooth?.id
      }/english.vtt`;
      fetch(captionsURL)
        .then((response: Response) => {
          if (response.status !== 200) {
            return resolve(rawBooth);
          }

          const formattedBooth: Booth = {
            ...rawBooth,
            english_captions_url: captionsURL
          };

          resolve(formattedBooth);
        })
        .catch(err => {
          loggerService.warn('Error locating captions file for a booth!', err);
          resolve(rawBooth);
        });
    });
  },
  setRawSpeakersFromUber(
    context: EventContext,
    { users, type = 'speaker' }: { users: any[]; type: 'speaker' | 'organizer' }
  ) {
    const storageKey = type === 'speaker' ? 'rawSpeakersFromUber' : 'rawOrganizersFromUber';
    if (Array.isArray(users) && users.length) {
      context.state.event[storageKey] = users
        .map((uberSpeakerData: { rank?: number; user: BareUser; hidden: Boolean }) => {
          const baseUser = {
            hidden: uberSpeakerData?.hidden || false,
            id: uberSpeakerData?.user?.id || '',
            rank: uberSpeakerData?.rank || 0
          };
          return baseUser;
        })
        .sort((userA: BareEventSpeaker, userB: BareEventSpeaker) => {
          return (userA?.rank || 0) - (userB?.rank || 0);
        });
    }
  },

  async loadEvent(context: EventContext, { eventID }: { eventID: string }) {
    return context.dispatch('loadEventFromUber', { eventID }).then(event => {
      const mmids = Array.from(
        new Set(Object.values(context.rootState.event.event?.users ?? {}).map((user: any) => user.mmid))
      );
      context.dispatch('mattermost/getUserStatuses', mmids, {
        root: true
      });

      return event;
    });
  },
  async loadPublicEvent(context: EventContext, { eventID }: { eventID: string }): Promise<UberResponse> {
    let event: any = null;
    let uberResponse: any = null;
    try {
      const response = await getEventFromUberEndpoint(eventID);
      uberResponse = response.data;
      event = uberResponse.event[0];
    } catch (e) {
      event = await retrievePublic({ id: eventID });
      loggerService.warn('CacheAPI: Failed to get event from uber endpoint. Fetched from public endpoint instead.', e);
    }

    const organization = uberResponse?.organization;
    context.commit('setOrganization', organization);

    context.commit('setEvent', { event });
    (context.commit as any)('event/avroom/updateJitterDelay', 10, { root: true });
    context.commit('setThemeStyles', context.rootState);

    context.dispatch('validateStoredVideoQuality');

    context.dispatch('mediaroom/checkUserPreferencesAndSetFilter', null, {
      root: true
    });
    context.dispatch('loadFonts');
    context.dispatch('setLocale', context.state.event);
    return uberResponse;
  },
  validateStoredVideoQuality(context: EventContext) {
    const useSkip480 = context.getters.useSkip480;
    const currentStoredQuality = context.rootState.user.selected_video_quality;
    let fallbackName: string | undefined;

    // This runs from loadEvent before Harness has answered, so the composite useFullHD getter is
    // still false at that point. Only the event-level setting is trustworthy here; publish-time
    // guards already cover the case where the 1080p flag itself is off.
    if (currentStoredQuality?.name === 'ULTRA_HIGH' && !context.state.event.enable_full_hd) {
      loggerService.warn('[VIDEO QUALITY] User had 1080p stored but Full HD not enabled, downgrading to 720p');
      fallbackName = 'HIGH';
    } else if (currentStoredQuality?.name === 'REGULAR' && useSkip480) {
      loggerService.warn('[VIDEO QUALITY] User had 480p stored but Use_Skip_480 is on, downgrading to 360p');
      fallbackName = 'MEDIUM';
    }

    if (fallbackName) {
      const fallbackQuality = AGORA.VIDEO_QUALITY.find(vq => vq.name === fallbackName) || AGORA.VIDEO_QUALITY[0];
      // The user module owns persistence of this preference, so go through its mutation.
      (context.commit as any)('setSelectedVideoQuality', fallbackQuality, { root: true });
    }
  },
  loadTracks(context: EventContext, payload: { eventID: string }) {
    return getTracks(payload.eventID).then(res => {
      context.commit('setTracks', res.data);
    });
  },
  async fetchEventById(context: EventContext, payload: { eventID: string }) {
    return context
      .dispatch('loadEvent', {
        ...payload
      })
      .then(event => {
        // load first page data for paginated pages
        if (event?.show_attendees || !event?.p2p_chat_disabled) {
          // For backend scalability we will no longer load eventnetwork early
          /*
          context.dispatch(
            "filterBar/loadNextPage",
            { page: "EventNetwork", firstLoad: true },
            { root: true }
          );
          */
        } else {
          (context.commit as any)(
            'filterBar/setPageAsLoaded',
            {
              page: 'EventNetwork'
            },
            { root: true }
          );
        }
        return event;
      });
  },
  async loadDiscussionGroups(context: EventContext, payload: { eventID: string }) {
    const eventID = payload?.eventID || context.rootState.route.params.eventID || context.state.event.id;
    if (eventID) {
      context.commit('setDiscussionGroups', {
        discussionGroups: []
      });
      const { data } = await loadDiscussionGroups({
        ...payload,
        event: eventID,
        is_breakout: false,
        is_booth: false,
        owner: null
      });
      context.commit('setDiscussionGroups', {
        discussionGroups: data.results
      });
      return data;
    }
    loggerService.warn('Tried to load discussion groups but found no eventId');
    return [];
  },

  loadDiscussionGroupByID(context: EventContext, groupid: string) {
    return loadDiscussionGroupByID(groupid).then(res => {
      context.commit('setIndividualDiscGroup', res.data);
    });
  },
  logout(context: EventContext) {
    // Tear down the sockets eagerly so they don't keep retrying against a session that's being logged out.
    context.dispatch('mattermost/disconnect', null, { root: true });
    context.dispatch('apigateway/disconnect', null, { root: true });
    clearSocketSessionMarkers();

    const coreUserId = context.rootState.user.currentUser?.id;
    const mattermostToken = mattermostApiClient.authToken;
    if (!coreUserId || !mattermostToken) {
      return;
    }
    return mmLogoutUser({ id: coreUserId, body: { mattermost_token: mattermostToken } })
      .then(() => {
        return safeSignout();
      })
      .then(() => {
        context.dispatch('RESET_USER', null, { root: true });

        if (window.sessionStorage.getItem(EMBEDDED_SESSION_STORAGE_KEY)) {
          pushRoute({
            name: 'EmbeddedEntry',
            query: {
              eventID: context.state.event.id,
              isEmbedded: 'true',
              emailId: undefined
            }
          });
          window.sessionStorage.removeItem(EMBEDDED_SESSION_STORAGE_KEY);
          sendEmbeddedEventMessage(EmbeddedMessageTypeEnum.LOGOUT_USER);
        } else {
          pushRoute({
            name: 'EventExit',
            query: {
              eventID: context.state.event.id
            }
          });
        }
      })
      .catch((err: any) => {
        loggerService.error('error signing out: ', err);
      });
  },
  loadRoomIfNotPresent(context: EventContext, groupid: string) {
    const group = context.state.discussionGroups.find(group => group.id === groupid);
    if (!group) {
      return context.dispatch('loadDiscussionGroupByID', groupid);
    }
    return Promise.resolve();
  },
  async loadParticipants(context: EventContext, params: any) {
    context.commit('setFilteredNetworkUsers', []);
    return context.dispatch('loadParticipantsWithoutBlanking', params);
  },
  async loadParticipantsWithoutBlanking(context: EventContext, params: any) {
    context.commit('markEventNetworkValid');
    try {
      const res = await getEventUsers({
        event: context.state.event.id || context.rootState.route.params.eventID,
        ...params
      });

      const data = {
        count: (res as { count?: number; results?: any[] }).count || 0,
        results:
          (res as { count?: number; results?: any[] }).results?.map((userObject: any) => {
            const transformed = transformParticipantToUser(userObject, context.rootState.route.params.eventID);
            return transformed;
          }) || []
      };

      context.commit('setTotalCount', data.count);
      const allmmids: any[] = data.results.map((it: any) => it?.user?.mmid);
      const users: EventUser[] = data.results as unknown as EventUser[];
      const count: number = data.count;
      context.dispatch('mattermost/getUserStatuses', allmmids, { root: true });
      const participants = makeUsers(users);
      if (params.page == 'EventNetwork') {
        context.commit('setFilteredNetworkUsers', participants);
      }

      return {
        results: participants,
        count: count
      };
    } catch (err) {
      loggerService.info('Marking eventnetwork as invalid because there was an error in fetching!');
      context.commit('invalidateEventNetwork');
    }
  },
  applyUberData(context: EventContext, data: any) {
    const eventUsersLoaded = Promise.resolve([context.rootState.user.currentEventUser]);
    context.commit('setLastUberFetchEpoch', Date.now());
    return saveData(context, eventUsersLoaded, data);
  },
  RESET(context: EventContext) {
    stopUberMmidPolling();
    stopUberPollingForRecorder();
    context.commit('cleanState');
  },
  updateAnimatedSlideStateFromBroadcast(context: EventContext, broadcast: Broadcast) {
    if (!broadcast) {
      return;
    }

    const isAnimatedSlidesEnabled = context.rootGetters['sidebar/isAnimatedSlidesEnabled'];
    if (!isAnimatedSlidesEnabled) {
      return;
    }

    const {
      current_playing_slide_asset,
      current_playing_slide_number,
      current_playing_step_number,
      slide_state_version,
      action
    } = broadcast as any;

    (context.commit as any)(
      'sidebar/updateAnimatedSlidePosition',
      {
        slideIndex: current_playing_slide_number,
        stepNumber: current_playing_step_number || 1,
        slide_state_version: slide_state_version || 1,
        action
      },
      { root: true }
    );

    context.commit('updateNewSlideAnimationAssets', {
      id: current_playing_slide_asset || null,
      current_playing_slide_number: current_playing_slide_number ?? 0,
      current_playing_step_number: current_playing_step_number ?? 0,
      slide_state_version: slide_state_version ?? 1,
      action
    });
  },
  resetRecordingForCurrentRoom(context: EventContext) {
    try {
      if (context.rootState.route?.params?.id) {
        const isDiscussionRoomPage = context.rootState.route?.fullPath?.includes('/discussion-room');
        const currentRouteID = context.rootState.route.params.id;
        const currentEvent = context.getters.currentEvent as any;
        const isP2PRoom = !!context.state.routeDiscussionGroup?.owner;
        const recorderDiscussionRoom = context.state.event?.record_discussion_rooms;
        if (currentEvent && isDiscussionRoomPage && !isP2PRoom && recorderDiscussionRoom) {
          return startDiscussionRoomRecording(currentRouteID);
        }
      }
    } catch (err) {
      loggerService.error('Error starting recording!', err);
    }
  },
  handleTransition(
    context: EventContext,
    payload: {
      item: AgendaItem;
      currentRouteId: string;
      itemBecomingLive: boolean;
      itemBecomingUnlive: boolean;
    }
  ) {
    const { item, currentRouteId, itemBecomingLive, itemBecomingUnlive } = payload;

    if (!itemBecomingLive && !itemBecomingUnlive) {
      // do nothing no need of any rediredtion or any toats
      loggerService.info('Not transitioning since nothing went live/unlive');
      return;
    }

    // update agenda liveness state at exact start time of the item - irrespective of redirection to stage.
    // exact start time is handled inside the updateAgendaLivenessState function
    context.dispatch('event/stage/updateAgendaLivenessState', item, { root: true });
    if (!itemBecomingUnlive) {
      loggerService.info('Not transitioning since nothing went unlive');
      return;
    }
    if (context.rootState.route.query?.record_mode) {
      loggerService.info('Not transitioning since record mode is on');
      return;
    }

    const agendaItemsForCurrentFollowedTrack = context.rootGetters['event/userTrackAgenda'];
    const indexOfCurrentAgendaItem = agendaItemsForCurrentFollowedTrack?.findIndex(
      (agendaItem: AgendaItem) => agendaItem.id == item.id
    );
    const isCurrentItemLastItemInFollowedTrack =
      indexOfCurrentAgendaItem === agendaItemsForCurrentFollowedTrack?.length - 1;
    const currentRoute = context.rootState.route;
    const ifUserIsOnBecomingUnliveItem = true;
    const isItemInFollowedTrack = context.rootGetters['event/isAgendaItemInUserTrack'](item.id);
    loggerService.info('session redirection: logging redirection conditions', {
      title: item.title,
      id: item.id,
      itemBecomingLive: itemBecomingLive,
      isCurrentItemLastItemInFollowedTrack: isCurrentItemLastItemInFollowedTrack,
      indexOfCurrentAgendaItem: indexOfCurrentAgendaItem
    });

    if (item.type === 'BROADCAST' && item.id !== currentRouteId) {
      loggerService.info('session redirection: not redirecting as some other broadcast is going live or unlive');
      return;
    }
    if (item.type !== 'BROADCAST' && !isItemInFollowedTrack) {
      loggerService.info(
        'session redirection: not redirecting to stage as non broadcast item is not in current track',
        item.title
      );
      return;
    }
    if (context.rootState.route.fullPath.includes('discussion-room')) {
      const currentDiscussionRoom = context.rootGetters['event/getDiscussionGroup'](currentRoute.params?.id);
      if (item.type === 'BOOTH' && !currentDiscussionRoom?.is_booth) {
        loggerService.info(
          'session redirection: not redirecting to stage as the item going unlive is booth but room is not booth'
        );
        return;
      }
      if (item.type === 'DISCUSSION_GROUP' && (currentDiscussionRoom.owner || currentDiscussionRoom?.is_booth)) {
        loggerService.info(
          'session redirection: not redirecting to stage as the item going unlive is DR but user is not inside the room'
        );
        return;
      }
    } else if (item.type !== 'BROADCAST') {
      if (
        ['EventBooth', 'EventBooth2'].includes(currentRoute.name?.toString() || '') &&
        (item.type === 'BOOTH' || item.type === 'BOOTH2')
      ) {
        loggerService.info(
          'session redirection: continuing redirection since user is on booth page and booth is going unlive'
        );
      } else {
        loggerService.info(
          'session redirection: not redirecting to stage as the item going unlive is not broadcast but user is not inside the room'
        );
        return;
      }
    }
    const isManualModeEnabled = context.state.event.manual_redirection_enabled;
    const hasAdminAccess =
      context.rootGetters['event/isBroadcastSpeakerOrOrganizer'] && !context.rootState.event.stage?.isTVMode;

    // redirection criteria:
    // 1. current session is becoming unlive
    // 2. current session is not the last session in the current user track
    // 3 current session is the session user is on

    const shouldUserLeaveSession = isManualModeEnabled || !isCurrentItemLastItemInFollowedTrack;

    const shouldRedirectToStage =
      itemBecomingUnlive && ifUserIsOnBecomingUnliveItem && shouldUserLeaveSession && !hasAdminAccess;

    if (shouldRedirectToStage) {
      loggerService.info('session redirection: redirecting to stage..', item.title);
      //resetting curtain track selected option to all before redirecting to stage
      context.dispatch(
        'event/stage/handleFiltersChange',
        {
          name: 'trackFilter',
          page: 'StageScreenSaver',
          payload: { value: 'All', label: 'All' }
        },
        { root: true }
      );

      pushRoute({
        name: 'stageRoll',
        query: {
          auto_redirect: 'true'
        }
      });
      return;
    }
    loggerService.info(
      'session redirection: not redirecting to stage as redirection critatia not fullflled..',
      item.title
    );
  },
  async handleAgendaUpdate(
    context: EventContext,
    { item, isBroadcast = false }: { item: AgendaItem; isBroadcast: boolean }
  ) {
    try {
      const itemType = isBroadcast ? 'BROADCAST' : getAgendaItemType(item);
      const storedItem = context.getters['agendaItem'](item.id, itemType);
      const mergedItem = storedItem ? { ...storedItem, ...item } : item;

      item = formatAgendaItem(mergedItem, context.state.event.tracks, context.state.event.users);

      if (isBroadcast) {
        item.type = 'BROADCAST';
      } else {
        item.type = getAgendaItemType(item);
      }

      if (isBroadcast) {
        context.dispatch('updateAnimatedSlideStateFromBroadcast', item);
      }

      const oldItem = context.getters['agendaItem'](item.id, item.type);
      loggerService.info('Old version of agenda item: ', oldItem);

      const wasLive = oldItem?.is_live;
      loggerService.info('agenda item live update handler: ', item.id, wasLive);

      let liveStatus;
      const itemBecomingLive = !wasLive && item.is_live;
      const itemBecomingUnlive = wasLive && !item.is_live;

      if (itemBecomingLive) {
        liveStatus = false; // Will be set after transition
      } else if (itemBecomingUnlive) {
        liveStatus = false;
      } else {
        liveStatus = item.is_live;
      }

      context.commit('updateAgendaItem', {
        ...item,
        is_live: liveStatus
      });

      context.dispatch('liveVideoRoom/handleAgendaVideoPlaybackState', null, {
        root: true
      });

      context.commit('setIsVideoQNARequestLoading', false);

      const currentRouteId = context.rootState.route.params.id;
      // handle transistion - based on flag

      context.dispatch('handleTransition', {
        item,
        currentRouteId,
        itemBecomingLive,
        itemBecomingUnlive
      });

      context.dispatch('event/survey/handleBroadcastSurveys', item.id, {
        root: true
      });
    } catch (err) {
      loggerService.error('Error handling agenda item update!', err);
    }
  },
  updateTrack(context: EventContext, track: any) {
    return new Promise((resolve, reject) => {
      const user = context.rootGetters.getCurrentUser as any;
      const currentTrack = context.getters.userTrack as any;
      if (track.disabled || !track || !track.id || track.id === currentTrack.id) {
        return resolve(true);
      }

      updateEventUser(
        context.rootState.event.event.id,
        user.id,
        {
          id: user.id,
          event: context.rootState.event.event.id,
          track: track.id
        },
        context.rootState.user.currentEventUser?.eventUser?.id
      )
        .then(() => {
          context.commit('updateCurrentEventUser' as any, { track: track.id }, { root: true });
          resolve(true);
        })
        .catch(e => {
          loggerService.error('Error changing user track', e);
          reject('Error changing user track');
        });
    });
  },
  async updateEvent(context: EventContext, { eventId, updatedParams }: { eventId: string; updatedParams: any }) {
    if (eventId && updatedParams) {
      return updateEvent(eventId, updatedParams)
        .then(() =>
          context.dispatch('loadEventFromUber', {
            eventID: eventId
          })
        )
        .catch(e => {
          loggerService.error('error in updateEvent', e);
          return Promise.reject('Event is not updated!!');
        });
    }
  },
  fetchActivePeopleCount(context: EventContext, eventId: string) {
    return getSessionOnlineAttendeeCounts(eventId).then(response => {
      context.commit('setActivePeople', response);
    });
  },
  getActiveEventUsers(context: EventContext, eventId: string) {
    return getSessionOnlineAttendeeCounts(eventId)
      .then(response => context.commit('setActiveUsersCount', response))
      .catch(error => {
        loggerService.error('getActiveEventUsers', error);
      });
  },
  moveToNextInEntryFlow(context: any) {
    const preEventFlow = context.getters.preEventFlow || [];
    const currentPage = context.rootState.route.name;
    let nextPage;

    if (preEventFlow.includes(currentPage)) {
      nextPage = preEventFlow[preEventFlow.indexOf(currentPage) + 1];
    }

    const eventInternalPage = context.state.fromSplashTo;

    if (!nextPage && eventInternalPage) {
      context.commit('setFromSplashTo', null);
      context
        .dispatch('event/stage/applyTransition', eventInternalPage, {
          root: true
        })
        .catch(() => gotoNextPage(context, ''));
    } else {
      gotoNextPage(context, nextPage);
    }
  },
  setAppDataOnBrowser(context: any) {
    const currentEvent = context.getters['currentEvent'];
    //Adding Favicon
    try {
      const favIconLinkElem: any = document.getElementById('appFavicon');
      if (favIconLinkElem) {
        if (currentEvent) {
          const favicon = currentEvent.favicon;
          if (favicon) {
            favIconLinkElem.href = favicon;
          } else {
            favIconLinkElem.href = `${window.location.origin}/logo.svg`;
          }
        }
      }
    } catch (err) {
      loggerService.error('error adding favicon', err);
    }
    //Adding Theme Color
    try {
      const themeColorElem: any = document.getElementById('appThemeColor');
      if (themeColorElem) {
        if (currentEvent) {
          const themeColor = currentEvent?.theme_obj?.theme_primary_color;
          if (themeColor) {
            themeColorElem.content = themeColor;
          } else {
            themeColorElem.content = '#27223f';
          }
        }
      }
    } catch (err) {
      loggerService.error('error adding theme color', err);
    }
  },
  showHybridModal(context: EventContext) {
    if (context.state.event.on_demand_mode) {
      return;
    }

    if (context.getters['isHybrid']) {
      const options = {
        ariaLabelledby: 'entry-switch-heading',
        cancelOnClickOutside: false,
        cancelOnEscape: false,
        role: 'dialog' as const,
        on: {
          onVirtual: () => {
            return context.dispatch('setInPerson', false);
          },
          onInPerson: () => {
            return context.dispatch('setInPerson', true);
          }
        }
      };
      DialogService.show(HybridDialog, {}, options);
    }
  },
  setInPerson(context: EventContext, value: Boolean) {
    const currentEventUser = context.rootState.user.currentEventUser;
    if (currentEventUser?.eventUser?.is_in_person != value) {
      updateEventUser(
        context.rootState.event.event.id,
        currentEventUser?.id,
        {
          id: currentEventUser?.id,
          event: context.rootState.event.event.id,
          is_in_person: value
        },
        currentEventUser?.eventUser?.id
      );
      context.commit(
        'updateCurrentEventUser' as any,
        {
          eventUser: {
            ...currentEventUser?.eventUser,
            is_in_person: value
          }
        },
        { root: true }
      );
    }
  },
  patchBroadcast(context: EventContext, { broadcastId, patch }: { broadcastId: string; patch: Partial<Broadcast> }) {
    // Makes an optimistic update locally
    // Reverts the change if the patch fails
    if (!broadcastId || !patch) {
      return Promise.reject('Invalid broadcast id or patch');
    }
    const existingBroadcast = (context.getters.currentEventBroadcast as any)(broadcastId);

    context.dispatch('handleAgendaUpdate', {
      item: {
        ...existingBroadcast,
        ...patch
      },
      isBroadcast: true
    });

    return updateBroadcast(broadcastId, patch)
      .then(() => {
        loggerService.info(`Broadcast ${broadcastId} updated`, patch);
      })
      .catch(err => {
        loggerService.warn(`Broadcast ${broadcastId} update failed. Reverting change.`, err, patch);
        context.dispatch('handleAgendaUpdate', {
          item: existingBroadcast,
          isBroadcast: true
        });
      });
  },
  checkBoothTabAndRedirect(context: EventContext, event: any) {
    if (event.show_event_booths === undefined) {
      return;
    }

    const isBoothTab = ['EventBooth', 'EventBoothsGridPage'].includes(context.rootState.route.name?.toString() || '');

    const isBoothTab2 = ['EventBooth2', 'EventBoothsGridPage2'].includes(
      context.rootState.route.name?.toString() || ''
    );

    if ((isBoothTab2 && event.booth_tab_title_2 === null) || (isBoothTab && !event.show_event_booths)) {
      DialogService.show(
        GenericMessageDialog,
        {
          title: i18n.global.t('@@translate@@ Booths Closed'),
          message: i18n.global.t(
            '@@translate@@ Your event organizer has closed all the booths. You will now be redirected to the stage'
          ),
          resolveLabel: i18n.global.t('@@translate@@ Return To Stage'),
          noCancel: true,
          id: 'boothsClosePopUp'
        },
        {
          cancelOnClickOutside: false,
          on: {
            onHide: async () => {
              pushRoute({
                name: 'EventLanding'
              });
            }
          }
        }
      );
    }
  },
  initOrganization(context: EventContext, organization: EventState['organization']) {
    context.commit('setOrganization', organization);

    if (getEnv('VUE_APP_LOGROCKET_ENABLED') === 'yes' && !organization?.anonymize_log_rocket) {
      const currentUser = context.rootGetters['getCurrentUser'];
      loggerService.identify(currentUser.id, {
        name: currentUser.name,
        email: currentUser.email,
        organization: organization?.name
      });
    }
  },

  async startManualSessionAction(context, { sessionId, sessionType }: ManualSessionRedirectionType) {
    try {
      DialogService.show(
        SessionChangeConfirmation,
        {
          title: '@@translate@@ Bring this session live?',
          confirmText: '@@translate@@ START SESSION',
          cancelText: '@@translate@@ Cancel'
        },
        {
          on: {
            onConfirm: () => onConfirm(context, { sessionId, sessionType, actionType: 'start' })
          }
        }
      );
    } catch (err) {
      loggerService.error('Error in startSessionRedirection', err);
    }
  },
  async endManualSessionAction(context, { sessionId, sessionType }: ManualSessionRedirectionType) {
    DialogService.show(
      SessionChangeConfirmation,
      {
        title: '@@translate@@ Bring this session to end?',
        confirmText: '@@translate@@ END SESSION',
        cancelText: '@@translate@@ Cancel'
      },
      {
        on: {
          onConfirm: () => onConfirm(context, { sessionId, sessionType, actionType: 'end' })
        }
      }
    );
  },
  manageLowerThird(context, { broadcastId, patch }: { broadcastId: string; patch: Partial<Broadcast> }) {
    // Makes an optimistic update locally
    // Reverts the change if the patch fails
    if (!broadcastId || !patch) {
      return Promise.reject('Invalid broadcast id or patch');
    }
    const existingBroadcast = (context.getters.currentEventBroadcast as any)(broadcastId);
    const isAStaffMember = context.rootGetters['isStaffMember'];

    context.dispatch('handleAgendaUpdate', {
      item: {
        ...existingBroadcast,
        ...patch
      },
      isBroadcast: true
    });

    let promise;
    if (isAStaffMember) {
      promise = toggleChat({
        id: broadcastId,
        body: {
          lower_third_spec: patch.lower_third_spec
        }
      });
    } else {
      promise = updateBroadcast(broadcastId, patch);
    }
    promise
      .then(() => {
        loggerService.info(`manageLowerThird: Broadcast ${broadcastId} updated`, patch);
      })
      .catch(err => {
        loggerService.warn(`manageLowerThird: Broadcast ${broadcastId} update failed. Reverting change.`, err, patch);
        context.dispatch('handleAgendaUpdate', {
          item: existingBroadcast,
          isBroadcast: true
        });
      });
  },
  async manageEventLevelTicker(
    context,
    { eventId, tickerParams }: { eventId: string; tickerParams: ToggleTickerRequestBody }
  ) {
    const isAStaffMember = context.rootGetters['isStaffMember'];
    if (isAStaffMember) {
      toggleTicker({
        id: eventId,
        body: {
          ticker_text: tickerParams.ticker_text,
          ticker_url: tickerParams.ticker_url,
          show_ticker: tickerParams.show_ticker
        }
      })
        .then(() =>
          context.dispatch('loadEventFromUber', {
            eventID: eventId
          })
        )
        .catch(e => {
          loggerService.error('error in Event Level Ticker', e);
          return Promise.reject('Failed to show Event Ticker');
        });
    } else {
      // TODO: Deprecate this after roles & permissions GA
      await context.dispatch('updateEvent', {
        eventId,
        updatedParams: tickerParams
      });
    }
  },

  async manageCue(context: EventContext, { broadcastId, patch }: { broadcastId: string; patch: Partial<Broadcast> }) {
    const isEventRolesEnabled = context.state.organization?.is_event_roles_enabled;
    if (!broadcastId || !patch) {
      return Promise.reject('Invalid broadcast id or patch');
    }
    const existingBroadcast = (context.getters.currentEventBroadcast as any)(broadcastId);

    context.dispatch('handleAgendaUpdate', {
      item: {
        ...existingBroadcast,
        ...patch
      },
      isBroadcast: true
    });

    let promise;
    if (isEventRolesEnabled) {
      promise = updateRunOfShowConfig({
        id: broadcastId,
        body: {
          is_automated_run_of_show: true,
          run_of_show_config: patch.run_of_show_config
        }
      });
    } else {
      promise = updateBroadcast(broadcastId, patch);
    }
    promise
      .then(() => {
        loggerService.info(`manageCue: Broadcast ${broadcastId} updated`, patch);
      })
      .catch(err => {
        loggerService.warn(`manageCue: Broadcast ${broadcastId} update failed. Reverting change.`, err, patch);
        context.dispatch('handleAgendaUpdate', {
          item: existingBroadcast,
          isBroadcast: true
        });
      });
  },

  async manageOnDemandCue(
    context: EventContext,
    {
      broadcastId,
      patch
    }: { broadcastId: string; patch: { on_demand_run_of_show_config: BroadcastUpdateOnDemandRunOfShowConfigRequest } }
  ) {
    if (!broadcastId || !patch) {
      return Promise.reject('Invalid broadcast id or patch');
    }

    try {
      await updateOnDemandRunOfShowConfig({
        id: broadcastId,
        body: {
          on_demand_run_of_show_config: patch.on_demand_run_of_show_config
        }
      });
    } catch (err: unknown) {
      loggerService.error(`manageOnDemandCue: Broadcast ${broadcastId} update failed.`, err, patch);
      throw err;
    }
  },

  // ============================================
  // Live Run of Show Cue Actions
  // ============================================

  async fetchLiveRunOfShowCues(context: EventContext) {
    const broadcastId = getBroadcastId(context, 'No valid broadcast ID available for fetching cues');
    const CUE_FETCH_LIMIT = 100;
    const CUE_FETCH_OFFSET = 0;

    context.commit('setCueLoading', true);
    context.commit('setCueError', null);

    try {
      const response = await listCues({
        broadcast_pk: broadcastId,
        queryParams: { limit: CUE_FETCH_LIMIT, offset: CUE_FETCH_OFFSET }
      });
      context.commit('setCues', response.results || []);
    } catch (error) {
      const errorMessage = (error as any).message || 'Failed to load cues';
      loggerService.error(errorMessage, { broadcastId, errorMessage });
      context.commit('setCueError', errorMessage);
      throw error;
    }
  },

  async loadLiveRunOfShowCuesWithSessionPreservation(context: EventContext) {
    const broadcastId = getBroadcastId(context, 'No valid broadcast ID available for loading cues');

    try {
      const existingSessionCues = context.state.liveRunOfShow.cues.filter(cue => {
        const action = cue.action as unknown as string;
        return (
          action === SESSION_ACTIONS.START || action === SESSION_ACTIONS.END || String(cue.id).startsWith('session_')
        );
      });

      await context.dispatch('fetchLiveRunOfShowCues');

      const currentBroadcast = context.rootGetters['event/currentBroadcast'];
      const isManualMode = currentBroadcast?.manual_redirection_enabled;

      if (isManualMode && existingSessionCues.length > 0) {
        const currentCues = context.state.liveRunOfShow.cues;
        const nonSessionCues = currentCues.filter(cue => {
          const action = cue.action as unknown as string;
          return !(
            action === SESSION_ACTIONS.START ||
            action === SESSION_ACTIONS.END ||
            String(cue.id).startsWith('session_')
          );
        });

        const mergedCues = [...nonSessionCues, ...existingSessionCues];
        context.commit('setCues', mergedCues);
      }
    } catch (error) {
      const errorMessage = (error as any).message || 'Failed to load cues with session preservation';
      loggerService.error(errorMessage, { broadcastId, errorMessage });
      throw error;
    }
  },

  async addLiveRunOfShowCue(context: EventContext, cueData: Omit<Cue, 'broadcast'>) {
    const broadcastId = getBroadcastId(context, 'No valid broadcast ID available for adding cue');

    try {
      const requestBody: Cue = {
        ...cueData,
        broadcast: broadcastId
      };

      const response = await createCue({
        broadcast_pk: broadcastId,
        body: requestBody as CueCreateRequest
      });

      context.commit('addCue', response);

      return response;
    } catch (error) {
      const errorMessage = (error as any).message || 'Failed to add cue';
      loggerService.error(errorMessage, {
        broadcastId,
        cueData,
        error
      });
      context.commit('setCueError', errorMessage);
      throw error;
    }
  },

  async deleteLiveRunOfShowCue(context: EventContext, cueId: string | number) {
    const broadcastId = getBroadcastId(context, 'No valid broadcast ID available for deleting cue');

    try {
      await deleteCue({
        id: Number(cueId)
      });

      loggerService.info('Cue deleted successfully', {
        broadcastId,
        cueId
      });

      context.commit('removeCue', cueId);

      return true;
    } catch (error) {
      const errorMessage = (error as any).message || 'Failed to delete cue';
      loggerService.error(errorMessage, {
        broadcastId,
        cueId,
        error
      });
      context.commit('setCueError', errorMessage);

      throw error;
    }
  },

  async updateLiveRunOfShowCue(context: EventContext, { cueId, updates }: { cueId: string | number; updates: any }) {
    const broadcastId = getBroadcastId(context, 'No valid broadcast ID available for updating cue');

    try {
      const response = await patchCue({
        id: Number(cueId),
        body: updates
      });
      context.commit('updateCue', response);

      return response;
    } catch (error) {
      const errorMessage = (error as any).message || 'Failed to update cue';
      loggerService.error(errorMessage, {
        broadcastId,
        cueId,
        updates
      });
      context.commit('setCueError', errorMessage);

      throw error;
    }
  },

  async updateLiveRunOfShowCueProperties(
    context: EventContext,
    {
      cueId,
      cueProperties
    }: {
      cueId: string | number;
      cueProperties: CuePropertyUpdateRequest[];
    }
  ) {
    const broadcastId = getBroadcastId(context, 'No valid broadcast ID available for updating cue properties');

    try {
      const response = await bulkUpdateCueProperties({
        id: Number(cueId),
        body: {
          cue_properties: cueProperties
        }
      });

      context.commit('updateCue', response);

      return response;
    } catch (error) {
      const errorMessage = (error as any).message || 'Failed to update cue properties';
      loggerService.error(errorMessage, {
        broadcastId,
        cueId,
        cueProperties
      });
      context.commit('setCueError', errorMessage);

      throw error;
    }
  },

  async resetLiveRunOfShowCues(context: EventContext, { broadcastId }: { broadcastId: string }) {
    try {
      context.commit('setCueLoading', true);
      await resetAllBroadcastsCues({
        broadcast_pk: broadcastId,
        body: {} as any
      });
      context.commit('setCueLoading', false);
    } catch (error) {
      context.commit('setCueLoading', false);
      context.commit('setCueError', (error as any)?.message || 'Failed to reset cues');
      throw error;
    }
  },

  async deleteAllLiveRunOfShowCues(context: EventContext, { broadcastId }: { broadcastId: string }) {
    try {
      await deleteAllBroadcastsCues({
        broadcast_pk: broadcastId,
        body: {} as any
      });

      context.commit('setCues', []);
    } catch (error) {
      context.commit('setCueError', (error as any)?.message || 'Failed to delete all cues');
      throw error;
    }
  },

  async reorderLiveRunOfShowCue(
    context: EventContext,
    {
      cueId,
      reorderData
    }: {
      cueId: number;
      reorderData: CueReorderRequest;
    }
  ) {
    const broadcastId = getBroadcastId(context, 'No valid broadcast ID available for reordering cue');

    try {
      const response = await reorderCue({
        id: cueId,
        body: reorderData
      });

      return response;
    } catch (error) {
      const errorMessage = (error as any).message || 'Failed to reorder cue';
      loggerService.error(errorMessage, {
        broadcastId,
        cueId,
        reorderData,
        error
      });
      context.commit('setCueError', errorMessage);
      throw error;
    }
  },

  closeEvent(context: EventContext) {
    DialogService.show(
      ConfirmationPopup,
      {
        title: '@@translate@@ Close Event',
        message:
          '@@translate@@ Closing the event will immediately end all sessions and remove all attendees from the event. Once closed, the event cannot be re-entered or restarted',
        confirmText: '@@translate@@ Yes Close Event for all',
        cancelText: '@@translate@@ Cancel',
        type: 'warning'
      },
      {
        on: {
          onConfirm: () => {
            endEvent(context.rootState.event.event?.id);
          }
        }
      }
    );
  },
  shareTicker(
    context: EventContext,
    { tickerText, tickerUrl, broadcastId }: { tickerText: string; tickerUrl: string; broadcastId: string }
  ) {
    context.commit('displayTicker', {
      tickerText,
      tickerUrl,
      broadcastId
    });
  },
  stopSharingTicker(context: EventContext, broadcastId: string) {
    context.commit('stopSharingTicker', broadcastId);
  },
  async fetchSurveyIframeConfig(context: EventContext) {
    const eventId = context.state.event?.id;
    if (!eventId) {
      return;
    }

    context.commit('setSurveyIframe', { loading: true });

    try {
      const { data } = await getSurveyIframeConfig(eventId);
      const survey = data as typeof data & CventSurveyIdentityFields;

      // Persist the Cvent identity ids the survey endpoint now returns (WEBINAR-67983)
      // regardless of whether the survey iframe itself is enabled.
      context.commit('setCventIdentity', {
        sessionId: survey.cvent_session_id ?? null,
        accountMappingId: survey.cvent_account_mapping_id ?? null
      });

      if (data.enabled && data.surveyurl) {
        context.commit('setSurveyIframe', { enabled: true, url: data.surveyurl });
      } else {
        context.commit('setSurveyIframe', { enabled: false, url: null });
      }
    } catch (error: any) {
      loggerService.error('Failed to fetch survey iframe config:', error);
      context.commit('setSurveyIframe', { enabled: false, url: null });
      context.commit('setCventIdentity', { sessionId: null, accountMappingId: null });
    } finally {
      context.commit('setSurveyIframe', { loading: false });
    }
  }
};

export function setButtonLoading(context, sessionId, actionType, value) {
  context.commit('updateAgendaItemLoadingState', {
    sessionId,
    loadingState: value,
    loadingKey: actionType === 'start' ? 'start_button' : 'end_button'
  });
}

export function formatAgenda(
  { broadcasts, items }: { broadcasts: Broadcast[]; items: AgendaItem[] },
  tracks: TrackItem[],
  users: User[]
) {
  return {
    broadcasts: broadcasts.map(make),
    items: items.map(make)
  };

  function make(item: AgendaItem | Broadcast) {
    return formatAgendaItem(item, tracks, users);
  }
}

export function setIsLiveAndTrigger(context: EventContext, item: AgendaItem | Broadcast) {
  const { timeLeftTillStart } = getToastDisplayDelay(item);
  // const liveStartTime = new Date(item.live_start_time);
  // const startTime = new Date(item.start_time).getTime();
  let now = Date.now();
  try {
    now = context.rootState.event.stage?.heartBeatDate?.getTime() || new Date().getTime();
  } catch (err) {
    loggerService.error(
      'Error when getting server adjusted time for redirection. Will proceeed with local time.',
      err,
      now
    );
  }
  const twoMin = 60 * 2 * 1000;
  const secondsTillStart = timeLeftTillStart / 1000;
  const startTimeIsUpto2MinFromNow = timeLeftTillStart > 0 && timeLeftTillStart <= twoMin;

  // The service that sets items to live automatically does it 2min or less before the
  // start time
  const itemWasAutoSetToLive = timeLeftTillStart <= twoMin;

  loggerService.info('Processing fake trigger of is_live: ', item.title, now);
  if (item.is_live && startTimeIsUpto2MinFromNow && itemWasAutoSetToLive) {
    // is_live is true but start_time is still upto 2 min in the future
    // then mimic a socket message coming in for is_live right now, because
    // this happens when the user reloads the browser after missing the automated
    // redirection socket message
    loggerService.info(
      `[simulive-debug] setIsLiveAndTrigger: Setting is_live=false for "${item.title}", will restore in ${timeLeftTillStart}ms. broadcast_type=${(item as any).broadcast_type}`
    );
    item.is_live = false;
    // This timeout makes sure that all other synchronous operations are finished in the store
    // with is_live set to false
    setTimeout(() => {
      if (getAgendaItemType(item) === 'BROADCAST') {
        context.dispatch(
          'event/handleAgendaUpdate',
          {
            item: { ...item, is_live: true },
            isBroadcast: true
          },
          { root: true }
        );
      } else {
        context.dispatch(
          'event/handleAgendaUpdate',
          {
            item: { ...item, is_live: true }
          },
          { root: true }
        );
      }
    }, timeLeftTillStart);
  } else if (item.is_live && !itemWasAutoSetToLive) {
    loggerService.info(
      'if item was not automatically set to live then we just want to redirect immediately so set is_live immediately',
      item.title
    );
  } else if (item.is_live && secondsTillStart <= 0) {
    // irrespective of if the item was set to live automatically or not,
    // if the start_time is in the past then redirect immediately
    loggerService.info('if item is live and start time is in the past then redirect immediately', item.title);
  }
}

export function mapTracksToTickets(tracks: TrackItem[], ticketTypes: TicketType[] = [], user: User): TrackItem[] {
  const ticket_codes = user.ticket_codes && user.ticket_codes.length > 0 ? user.ticket_codes : ['DEFAULT'];

  return tracks.map(track => {
    const tickets = ticketTypes.filter(ticket => ticket.allowed_tracks.includes(track.id));

    const disabled =
      !user.status?.isOrganizer &&
      tickets.length > 0 &&
      !ticket_codes.includes('ALL') &&
      !ticket_codes.reduce(
        (acc, code) => acc || !!ticketTypes.find(ticket => ticket.code === code)?.allowed_tracks?.includes(track.id),
        false
      );

    loggerService.info('mapTracksToTickets', {
      disabled,
      track,
      ticket_codes,
      ticketTypes,
      tickets
    });

    return { ...track, tickets, disabled };
  });
}

export default actions;
